import { Folder } from '@/types';
import { Folder as FolderIcon, Tag, Trash2, ChevronDown, ChevronRight, Inbox, AlertCircle, Tags, RefreshCw, Menu, Images, Code, User, Palette, TriangleAlert, Lock, Plus } from 'lucide-react';
import { BsImages, BsLightbulb, BsPalette, BsCamera, BsBoxSeam } from 'react-icons/bs';
import { useState, useEffect } from 'react';
import logo from "@/assets/logo.png";

import { HoverCard, HoverCardContent, HoverCardTrigger } from '@/components/ui/hover-card';
import { useIsMobile } from '@/hooks/use-mobile';
import { formatNumber } from '@/utils/formatNumber';
import { ReprocessNoTagsDialog } from '@/components/ReprocessNoTagsDialog';
import { ActiveJobs } from '@/components/ActiveJobs';
import { Button } from '@/components/ui/button';

interface AppSidebarProps {
  folders: Folder[];
  selectedFolderId: string | null;
  onFolderSelect: (folderId: string | null) => void;
  onToggleFolder: (folderId: string) => void;
  onCreateFolder?: () => void;
  totalPhotos: number;
  trashCount: number;
  piiCount: number;
  noTagsCount: number;
  noFoldersCount: number;
  hasCodeCount: number;
  hasPersonCount: number;
  illustratedCount: number;
  errorCount: number;
  onShowTrash: () => void;
  onShowAll: () => void;
  onShowPII: () => void;
  onShowNoTags: () => void;
  onShowNoFolders: () => void;
  onShowHasCode: () => void;
  onShowHasPerson: () => void;
  onShowIllustrated: () => void;
  onShowError: () => void;
  showTrash: boolean;
  activeQuickLink: 'all' | 'trash' | 'pii' | 'notags' | 'nofolders' | 'hascode' | 'hasperson' | 'illustrated' | 'error' | null;
  photoTypeFilter?: 'all' | 'has_pii' | 'has_tag' | 'no_tags' | 'no_folders' | 'has_code' | 'has_person' | 'is_illustrated' | 'has_error' | null;
  organizationId?: string;
  viewMode?: 'desktop' | 'icon';
  onViewModeChange?: (mode: 'desktop' | 'icon') => void;
  totalStorageBytes?: number;
}

export function AppSidebar({
  folders,
  selectedFolderId,
  onFolderSelect,
  onToggleFolder,
  onCreateFolder,
  totalPhotos,
  trashCount,
  piiCount,
  noTagsCount,
  noFoldersCount,
  hasCodeCount,
  hasPersonCount,
  illustratedCount,
  errorCount,
  onShowTrash,
  onShowAll,
  onShowPII,
  onShowNoTags,
  onShowNoFolders,
  onShowHasCode,
  onShowHasPerson,
  onShowIllustrated,
  onShowError,
  showTrash,
  activeQuickLink,
  photoTypeFilter = 'all',
  organizationId = '',
  viewMode = 'desktop',
  onViewModeChange,
  totalStorageBytes = 0,
}: AppSidebarProps) {
  const isMobile = useIsMobile();
  const [openPopover, setOpenPopover] = useState<string | null>(null);
  const [isTabletMode, setIsTabletMode] = useState(false);
  const [showReprocessDialog, setShowReprocessDialog] = useState(false);
  
  // Check if we're in tablet mode (between md and lg breakpoints)
  useEffect(() => {
    const checkTabletMode = () => {
      setIsTabletMode(window.innerWidth >= 768 && window.innerWidth < 1024);
    };
    
    checkTabletMode();
    window.addEventListener('resize', checkTabletMode);
    
    return () => window.removeEventListener('resize', checkTabletMode);
  }, []);

  const getFolderIcon = (folder: Folder) => {
    const iconSize = 'h-6 w-6'; // Always large icons
    if (folder.icon === '💡') return <BsLightbulb className={`${iconSize} flex-shrink-0`} style={{ color: folder.color }} />;
    if (folder.icon === '📦') return <BsBoxSeam className={`${iconSize} flex-shrink-0`} style={{ color: folder.color }} />;
    if (folder.name.toLowerCase().includes('photo')) return <BsCamera className={`${iconSize} flex-shrink-0`} style={{ color: folder.color }} />;
    if (folder.name.toLowerCase().includes('illustration')) return <BsPalette className={`${iconSize} flex-shrink-0`} style={{ color: folder.color }} />;
    return <FolderIcon className={`${iconSize} flex-shrink-0`} style={{ color: folder.color }} />;
  };

  const renderFolders = (parentId: string | null, depth: number = 0): React.ReactNode => {
    // Only show top-level folders (albums) - parentId should be null
    const currentLevelFolders = folders.filter(f => f.parentId === parentId);

    return currentLevelFolders.map(folder => {
      const isSelected = selectedFolderId === folder.id;
      const coverUrl = (folder as any).cover_url; // Cover photo URL from database

      return (
        <div key={folder.id}>
          {viewMode === 'icon' || isTabletMode ? (
            <HoverCard openDelay={0} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  onClick={() => {
                    onFolderSelect(folder.id);
                    setOpenPopover(null);
                  }}
                  className={`
                    w-full flex flex-col gap-1 px-2 py-2 text-sm rounded-md
                    transition-colors hover:bg-secondary/50
                    ${isSelected ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  `}
                  title={folder.name}
                >
                  {/* Cover Image */}
                  <div className="w-full aspect-square rounded-md overflow-hidden bg-muted flex items-center justify-center">
                    {coverUrl ? (
                      <img src={coverUrl} alt={folder.name} className="w-full h-full object-cover" />
                    ) : (
                      getFolderIcon(folder)
                    )}
                  </div>
                  
                  {/* Hide label in icon mode */}
                  {viewMode === 'desktop' && (
                    <>
                      <span className="text-xs truncate w-full text-center lg:hidden md:block">{folder.name}</span>
                      {folder.photoCount > 0 && (
                        <span className="text-xs text-muted-foreground lg:hidden md:block">{folder.photoCount}</span>
                      )}
                    </>
                  )}
                </button>
              </HoverCardTrigger>
              <HoverCardContent side="right" align="start" className="w-48 p-2">
                <div className="space-y-1">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    {getFolderIcon(folder)}
                    <span>{folder.name}</span>
                    {folder.photoCount > 0 && (
                      <span className="ml-auto text-xs text-muted-foreground">{folder.photoCount}</span>
                    )}
                  </div>
                </div>
              </HoverCardContent>
            </HoverCard>
          ) : (
            <button
              onClick={() => {
                onFolderSelect(folder.id);
                setOpenPopover(null);
              }}
              className={`
                w-full flex items-center gap-2 px-2 py-2 text-sm rounded-md
                transition-colors hover:bg-secondary/50
                ${isSelected ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                lg:justify-start md:justify-center
              `}
              title={folder.name}
            >
              {/* Cover Image Thumbnail */}
              <div className="w-10 h-10 rounded-md overflow-hidden bg-muted flex-shrink-0 flex items-center justify-center">
                {coverUrl ? (
                  <img src={coverUrl} alt={folder.name} className="w-full h-full object-cover" />
                ) : (
                  getFolderIcon(folder)
                )}
              </div>
              
              <span className="truncate flex-1 text-left lg:block md:hidden">{folder.name}</span>
              
              {folder.photoCount > 0 && (
                <span className="text-xs text-muted-foreground flex-shrink-0 lg:block md:hidden">
                  {folder.photoCount}
                </span>
              )}
            </button>
          )}
        </div>
      );
    });
  };

  return (
    <aside className={`h-screen bg-card border-r border-border flex flex-col transition-all duration-200 ${viewMode === 'icon' ? 'w-16' : 'lg:w-64 md:w-16'}`}>
      <div className="h-14 flex items-center px-4 border-b border-border">
        {viewMode === 'icon' ? (
          <div className="flex items-center justify-center w-full">
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10"
              onClick={() => onViewModeChange?.('desktop')}
              title="Expand to Desktop View"
            >
              <Menu className="h-8 w-8" />
            </Button>
          </div>
        ) : (
          <div className="flex items-center justify-between gap-2 w-full">
            <div className="flex items-center gap-2 lg:justify-start md:justify-center">
              <img src={logo} alt="Pixel Recall" className="h-6 w-6 flex-shrink-0 rounded-md" />
              <span className="text-base font-semibold lg:block md:hidden">Pixel Recall</span>
            </div>
            <Button
              variant="ghost"
              size="icon"
              className="h-10 w-10 lg:flex md:hidden max-md:hidden flex-shrink-0"
              onClick={() => onViewModeChange?.('icon')}
              title="Collapse to Icon View"
            >
              <Menu className="h-8 w-8" />
            </Button>
          </div>
        )}
      </div>

      <div className="flex-1 overflow-y-auto p-3 space-y-1 pb-16">
        {/* Quick Links */}
        <div className="mb-4">
          <div className={`px-3 py-2 text-xs font-semibold text-muted-foreground uppercase tracking-wider ${viewMode === 'icon' ? 'hidden' : 'lg:block md:hidden'}`}>
            Collections
          </div>
          
{viewMode === 'icon' || isTabletMode ? (
            <HoverCard openDelay={0} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  onClick={() => {
                    onShowAll();
                    setOpenPopover(null);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                    transition-colors hover:bg-secondary/50
                    ${activeQuickLink === 'all' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                    ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                  `}
                  title="All Photos"
                >
                  <Images className="h-6 w-6 flex-shrink-0" />
                  <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>All Photos</span>
                  <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(totalPhotos)}</span>
                </button>
              </HoverCardTrigger>
              <HoverCardContent side="right" align="start" className="w-48 p-2">
                <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                  <Images className="h-4 w-4" />
                  <span>All Photos</span>
                  <span className="ml-auto text-xs text-muted-foreground">{formatNumber(totalPhotos)}</span>
                </div>
              </HoverCardContent>
            </HoverCard>
          ) : (
            <button
              onClick={onShowAll}
              className={`
                w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                transition-colors hover:bg-secondary/50
                ${activeQuickLink === 'all' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                lg:justify-start md:justify-center
              `}
              title="All Photos"
            >
                <Images className="h-6 w-6 flex-shrink-0" />
                <span className="flex-1 text-left lg:block md:hidden">All Photos</span>
                <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(totalPhotos)}</span>
              </button>
          )}


          {viewMode === 'icon' || isTabletMode ? (
            <>
              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => {
                      onShowNoTags();
                      setOpenPopover(null);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                      transition-colors hover:bg-secondary/50
                      ${activeQuickLink === 'notags' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                      ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                    `}
                    title="No Tags"
                  >
                    <Tags className="h-6 w-6 flex-shrink-0" />
                    <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>No Tags</span>
                    <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(noTagsCount)}</span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-48 p-2">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    <Tags className="h-4 w-4" />
                    <span>No Tags</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatNumber(noTagsCount)}</span>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => {
                      onShowNoFolders();
                      setOpenPopover(null);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                      transition-colors hover:bg-secondary/50
                      ${activeQuickLink === 'nofolders' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                      ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                    `}
                    title="No Folders"
                  >
                    <FolderIcon className="h-6 w-6 flex-shrink-0" />
                    <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>No Folders</span>
                    <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(noFoldersCount)}</span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-48 p-2">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    <FolderIcon className="h-4 w-4" />
                    <span>No Folders</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatNumber(noFoldersCount)}</span>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => {
                      onShowPII();
                      setOpenPopover(null);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                      transition-colors hover:bg-secondary/50
                      ${activeQuickLink === 'pii' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                      ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                    `}
                    title="PII"
                  >
                    <Lock className="h-6 w-6 flex-shrink-0" />
                    <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>PII</span>
                    <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(piiCount)}</span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-48 p-2">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    <Lock className="h-4 w-4" />
                    <span>PII</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatNumber(piiCount)}</span>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => {
                      onShowHasCode();
                      setOpenPopover(null);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                      transition-colors hover:bg-secondary/50
                      ${activeQuickLink === 'hascode' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                      ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                    `}
                    title="Code"
                  >
                    <Code className="h-6 w-6 flex-shrink-0" />
                    <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>Code</span>
                    <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(hasCodeCount)}</span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-48 p-2">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    <Code className="h-4 w-4" />
                    <span>Code</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatNumber(hasCodeCount)}</span>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => {
                      onShowHasPerson();
                      setOpenPopover(null);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                      transition-colors hover:bg-secondary/50
                      ${activeQuickLink === 'hasperson' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                      ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                    `}
                    title="Person"
                  >
                    <User className="h-6 w-6 flex-shrink-0" />
                    <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>Person</span>
                    <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(hasPersonCount)}</span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-48 p-2">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    <User className="h-4 w-4" />
                    <span>Person</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatNumber(hasPersonCount)}</span>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => {
                      onShowIllustrated();
                      setOpenPopover(null);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                      transition-colors hover:bg-secondary/50
                      ${activeQuickLink === 'illustrated' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                      ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                    `}
                    title="Illustrated"
                  >
                    <Palette className="h-6 w-6 flex-shrink-0" />
                    <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>Illustrated</span>
                    <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(illustratedCount)}</span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-48 p-2">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    <Palette className="h-4 w-4" />
                    <span>Illustrated</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatNumber(illustratedCount)}</span>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                    onClick={() => {
                      onShowError();
                      setOpenPopover(null);
                    }}
                    className={`
                      w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                      transition-colors hover:bg-secondary/50
                      ${activeQuickLink === 'error' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                      ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                    `}
                    title="Error"
                  >
                    <TriangleAlert className="h-6 w-6 flex-shrink-0 text-white" />
                    <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>Error</span>
                    <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(errorCount)}</span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-48 p-2">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    <TriangleAlert className="h-4 w-4 text-white" />
                    <span>Error</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatNumber(errorCount)}</span>
                  </div>
                </HoverCardContent>
              </HoverCard>

              <HoverCard openDelay={0} closeDelay={100}>
                <HoverCardTrigger asChild>
                  <button
                  onClick={() => {
                    onShowTrash();
                    setOpenPopover(null);
                  }}
                  className={`
                    w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                    transition-colors hover:bg-secondary/50
                    ${showTrash ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                    ${viewMode === 'icon' ? 'justify-center' : 'lg:justify-start md:justify-center'}
                  `}
                    title="Deleted"
                  >
                    <Trash2 className="h-6 w-6 flex-shrink-0" />
                    <span className={`flex-1 text-left ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>Deleted</span>
                    <span className={`text-xs text-muted-foreground ${viewMode === 'desktop' ? 'lg:block md:hidden' : 'hidden'}`}>{formatNumber(trashCount)}</span>
                  </button>
                </HoverCardTrigger>
                <HoverCardContent side="right" align="start" className="w-48 p-2">
                  <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                    <Trash2 className="h-4 w-4" />
                    <span>Deleted</span>
                    <span className="ml-auto text-xs text-muted-foreground">{formatNumber(trashCount)}</span>
                  </div>
                </HoverCardContent>
              </HoverCard>
            </>
          ) : (
            <>
              <button
                onClick={onShowNoTags}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                  transition-colors hover:bg-secondary/50
                  ${activeQuickLink === 'notags' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  lg:justify-start md:justify-center
                `}
                title="No Tags"
              >
                <Tags className="h-6 w-6 flex-shrink-0" />
                <span className="flex-1 text-left lg:block md:hidden">No Tags</span>
                <div className="flex items-center gap-2">
                  <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(noTagsCount)}</span>
                  {noTagsCount > 0 && (
                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        setShowReprocessDialog(true);
                      }}
                      className="p-1 hover:bg-primary/10 rounded transition-colors lg:block md:hidden"
                      title="Reprocess photos without tags"
                    >
                      <RefreshCw className="h-3 w-3" />
                    </button>
                  )}
                </div>
              </button>

              <button
                onClick={onShowNoFolders}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                  transition-colors hover:bg-secondary/50
                  ${photoTypeFilter === 'no_folders' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  lg:justify-start md:justify-center
                `}
                title="No Folders"
              >
                <FolderIcon className="h-6 w-6 flex-shrink-0" />
                <span className="flex-1 text-left lg:block md:hidden">No Folders</span>
                <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(noFoldersCount)}</span>
              </button>

              <button
                onClick={onShowPII}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                  transition-colors hover:bg-secondary/50
                  ${activeQuickLink === 'pii' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  lg:justify-start md:justify-center
                `}
                title="PII"
              >
                <Lock className={`h-6 w-6 flex-shrink-0 ${activeQuickLink === 'pii' ? 'text-primary' : ''}`} />
                <span className="flex-1 text-left lg:block md:hidden">PII</span>
                <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(piiCount)}</span>
              </button>

              <button
                onClick={onShowHasCode}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                  transition-colors hover:bg-secondary/50
                  ${activeQuickLink === 'hascode' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  lg:justify-start md:justify-center
                `}
                title="Code"
              >
                <Code className="h-6 w-6 flex-shrink-0" />
                <span className="flex-1 text-left lg:block md:hidden">Code</span>
                <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(hasCodeCount)}</span>
              </button>

              <button
                onClick={onShowHasPerson}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                  transition-colors hover:bg-secondary/50
                  ${activeQuickLink === 'hasperson' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  lg:justify-start md:justify-center
                `}
                title="Person"
              >
                <User className="h-6 w-6 flex-shrink-0" />
                <span className="flex-1 text-left lg:block md:hidden">Person</span>
                <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(hasPersonCount)}</span>
              </button>

              <button
                onClick={onShowIllustrated}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                  transition-colors hover:bg-secondary/50
                  ${activeQuickLink === 'illustrated' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  lg:justify-start md:justify-center
                `}
                title="Illustrated"
              >
                <Palette className="h-6 w-6 flex-shrink-0" />
                <span className="flex-1 text-left lg:block md:hidden">Illustrated</span>
                <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(illustratedCount)}</span>
              </button>

              <button
                onClick={onShowError}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                  transition-colors hover:bg-secondary/50
                  ${activeQuickLink === 'error' ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  lg:justify-start md:justify-center
                `}
                title="Error"
              >
                <TriangleAlert className="h-6 w-6 flex-shrink-0 text-white" />
                <span className="flex-1 text-left lg:block md:hidden">Error</span>
                <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(errorCount)}</span>
              </button>

              <button
                onClick={onShowTrash}
                className={`
                  w-full flex items-center gap-3 px-3 py-2 text-sm rounded-md
                  transition-colors hover:bg-secondary/50
                  ${showTrash ? 'bg-secondary text-foreground' : 'text-muted-foreground'}
                  lg:justify-start md:justify-center
                `}
                title="Deleted"
              >
                <Trash2 className="h-6 w-6 flex-shrink-0" />
                <span className="flex-1 text-left lg:block md:hidden">Deleted</span>
                <span className="text-xs text-muted-foreground lg:block md:hidden">{formatNumber(trashCount)}</span>
              </button>
            </>
          )}
        </div>

        {/* Folders */}
        <div>
          <div className={`flex items-center justify-between px-3 py-2 ${viewMode === 'icon' ? 'hidden' : 'lg:flex md:hidden'}`}>
            <div className="text-xs font-semibold text-muted-foreground uppercase tracking-wider">
              Folders
            </div>
            {onCreateFolder && (
              <Button
                variant="ghost"
                size="icon"
                className="h-6 w-6"
                onClick={onCreateFolder}
                title="Create new folder"
              >
                <Plus className="h-4 w-4" />
              </Button>
            )}
          </div>
          
          {/* Icon mode: Show + button */}
          {(viewMode === 'icon' || isTabletMode) && onCreateFolder && (
            <HoverCard openDelay={0} closeDelay={100}>
              <HoverCardTrigger asChild>
                <button
                  onClick={onCreateFolder}
                  className="w-full flex items-center justify-center px-3 py-2 text-sm rounded-md transition-colors hover:bg-secondary/50 text-muted-foreground"
                  title="Create new folder"
                >
                  <Plus className="h-6 w-6" />
                </button>
              </HoverCardTrigger>
              <HoverCardContent side="right" align="start" className="w-48 p-2">
                <div className="flex items-center gap-2 px-2 py-1.5 text-sm font-medium">
                  <Plus className="h-4 w-4" />
                  <span>Create Folder</span>
                </div>
              </HoverCardContent>
            </HoverCard>
          )}
          
          {renderFolders(null)}
        </div>

        {/* Active Jobs - Above Storage */}
        <ActiveJobs organizationId={organizationId} viewMode={viewMode} />

        {/* Storage Info - Sticky at bottom */}
        <div
          className={`fixed bottom-0 left-0 right-0 border-t bg-card pt-2 px-3 pb-2 ${isMobile ? 'w-full' : (viewMode === 'icon' ? 'w-[64px]' : 'md:w-[256px]')}`}
        >
          <div className={`text-xs text-muted-foreground ${isMobile ? 'block' : (viewMode === 'icon' ? 'hidden' : 'block')}`}>
            <div className="flex justify-between items-center">
              <span>Storage:</span>
              <span className="font-medium">{formatBytes(totalStorageBytes)}</span>
            </div>
          </div>
        </div>
      </div>

      <ReprocessNoTagsDialog
        open={showReprocessDialog}
        onOpenChange={setShowReprocessDialog}
        organizationId={organizationId}
        photoCount={noTagsCount}
      />
    </aside>
  );
}

function formatBytes(bytes: number): string {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return Math.round((bytes / Math.pow(k, i)) * 100) / 100 + ' ' + sizes[i];
}

